import styled, { keyframes } from 'styled-components'
import { COLORS, SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'

export const Card = styled.div`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  overflow: hidden;
`

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md};
  padding: ${SPACING.xl};
  border-bottom: 1px solid var(--color-border);
`

export const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

export const SummaryTitle = styled.h3`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin: 0;
  line-height: 1.4;
`

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  flex-wrap: wrap;
`

export const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
`

// ─── Headline finding ─────────────────────────────────────────────────────────

export const HeadlineBlock = styled.div`
  padding: ${SPACING.xl};
  background: color-mix(in srgb, ${COLORS.purple500} 6%, transparent);
  border-bottom: 1px solid var(--color-border);
`

export const HeadlineLabel = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${COLORS.purple600};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${SPACING.sm};
`

export const HeadlineText = styled.p`
  font-size: ${TYPOGRAPHY.h4.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-primary);
  line-height: 1.5;
  margin: 0;
`

// ─── Sections ─────────────────────────────────────────────────────────────────

export const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
`

export const Section = styled.div`
  padding: ${SPACING.xl};

  & + & {
    border-top: 1px solid var(--color-border);
  }
`

export const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${SPACING.md};
`

export const FactList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

export const FactLine = styled.li`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-primary);
  line-height: 1.55;
  padding-left: ${SPACING.md};
  position: relative;

  &::before {
    content: '·';
    position: absolute;
    left: 0;
    color: var(--color-text-secondary);
  }
`

export const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
`

export const ListItem = styled.li`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-primary);
  line-height: 1.55;
  padding-left: ${SPACING.md};
  position: relative;

  &::before {
    content: '·';
    position: absolute;
    left: 0;
    color: var(--color-text-secondary);
  }
`

// ─── Loading / pending ────────────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`

export const PendingState = styled.div`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-secondary);
  padding: ${SPACING.xxl} ${SPACING.xl};
  text-align: center;
  animation: ${pulse} 2s ease-in-out infinite;
`

// ─── Footer ──────────────────────────────────────────────────────────────────

export const CardFooter = styled.div`
  padding: ${SPACING.md} ${SPACING.xl};
  border-top: 1px solid var(--color-border);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
`
